import { Link, useParams } from "react-router-dom";
import { groups } from "../data";
import { detailContent } from "../content";
import { ArrowIcon } from "../components/Icons";
import Seo from "../components/Seo";
import NotFoundPage from "./NotFoundPage";

export default function DetailPage() {
  const { group, slug } = useParams();
  const data = groups[group];
  const item = data?.items.find((candidate) => candidate.slug === slug);
  const content = detailContent[group]?.[slug];

  if (!data || !item || !content) return <NotFoundPage />;

  const primaryLabel =
    group === "product-line"
      ? "Request product information"
      : group === "pipeline"
        ? "Discuss partnering"
        : "Discuss a program";

  return (
    <>
      <Seo title={item.title} description={item.description} />
      <section className="detail-hero" id="overview">
        <div className="detail-hero-copy">
          <Link className="breadcrumb" to={`/${group}`}>
            {group.replace("-", " ")} <span>/</span>
          </Link>
          <p className="detail-page-name">{item.title}</p>
          <h1>{content.headline}</h1>
          <p className="inner-lead">{item.description}</p>
          <div className="button-row">
            <Link className="button button-primary" to={group === "product-line" ? "/contact" : "/partnership"}>
              {primaryLabel} <ArrowIcon />
            </Link>
            <Link className="button button-secondary" to="/contact">Request information <ArrowIcon /></Link>
          </div>
        </div>
        <div className="detail-visual">
          <div className={`detail-hero-media hero-media-${group}`}>
            <img src={item.image || data.image} alt={`${item.title} scientific visualization`} fetchPriority="high" decoding="async" />
            <img className="detail-icon" src={item.icon} alt="" />
          </div>
          <ol className="detail-signal-list" aria-label={`${item.title} focus areas`}>
            {item.points.map((point, index) => (
              <li className={index === 0 ? "is-active" : ""} key={point}>{point}</li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section detail-body" id="capabilities">
        <div className="detail-statement">
          <p className="section-label">Overview</p>
          <h2>{content.introTitle}</h2>
          <p>{content.intro}</p>
        </div>
        <div className="detail-points">
          <p className="section-label">{content.featureTitle}</p>
          {content.features.map((feature, index) => (
            <article key={feature.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="process-section" id="workflow">
        <div className="process-heading">
          <p className="section-label">Workflow</p>
          <h2>{content.flowTitle}</h2>
          <p>Each transition creates a review point for evidence, assumptions, ownership, and next-step decisions.</p>
        </div>
        <ol className="process-steps">
          {content.flow.map((step, index) => (
            <li key={step}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{step}</strong>
            </li>
          ))}
        </ol>
      </section>

      <section className="section context-section" id="program-fit">
        <div className="section-heading split-heading">
          <div>
            <p className="section-label">Program fit</p>
            <h2>{content.contextsTitle}</h2>
          </div>
          <p>
            Public examples describe where a conversation may begin. Final scope, evidence requirements,
            availability, and access are confirmed through review.
          </p>
        </div>
        <div className="context-grid">
          {content.contexts.map((context, index) => (
            <article key={context.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h3>{context.title}</h3>
              <p>{context.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="evidence-table-section" id="evidence">
        <div>
          <p className="section-label">Public evidence framework</p>
          <h2>What this page can—and cannot—communicate.</h2>
        </div>
        <div className="evidence-table" role="table" aria-label="Evidence framework">
          {content.evidence.map((row) => (
            <div role="row" key={row[0]}>
              <strong role="cell">{row[0]}</strong>
              <span role="cell">{row[1]}</span>
              <span role="cell">{row[2]}</span>
            </div>
          ))}
        </div>
      </section>

      {content.related.length ? (
        <section className="section related-section">
          <div>
            <p className="section-label">Connected pages</p>
            <h2>Continue through the Medein system.</h2>
          </div>
          <div className="related-links">
            {content.related.map(([label, path, text]) => (
              <Link to={path} key={path}>
                <span>{label}</span>
                <strong>{text}</strong>
                <ArrowIcon />
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="detail-cta">
        <div>
          <p className="section-label">Collaboration</p>
          <h2>{content.ctaTitle}</h2>
          <p>{content.ctaText}</p>
        </div>
        <Link className="button button-light" to="/partnership">Partner With Us <ArrowIcon /></Link>
      </section>
    </>
  );
}
